import {
  PnoeMeasurementCatalogs,
  PnoeMeasurementStatus,
  PnoeMeasurementType,
  PnoePhaseType,
  PnoeReportStatus,
  toNumber,
} from "../entities/pnoeMeasurement";
import { ResolvedStatus, StatusTone } from "./labPanelStatus";

/**
 * Display metadata for PNOĒ breath-test measurements.
 *
 * Deliberately mirrors `labPanelStatus.ts` so the two Orders listings look and
 * behave alike: this module resolves `{ key, label, tone, actionRequired }` and
 * then reuses that file's `getStatusBadgeStyle` / `getStatusIconColor` /
 * `getRowAccentColor` for the presentation, rather than defining its own colors.
 *
 * Labels come from the API's `configuration/catalogs`. The fallback maps below
 * exist so a failed catalogs call degrades to readable English instead of raw
 * enum values (`in_progress`).
 */

const MEASUREMENT_STATUS_FALLBACK: Record<PnoeMeasurementStatus, string> = {
  in_progress: "In progress",
  completed: "Completed",
  invalidated: "Invalidated",
};

const REPORT_STATUS_FALLBACK: Record<PnoeReportStatus, string> = {
  not_started: "Not started",
  pending: "Pending",
  ready: "Ready",
  failed: "Failed",
};

const MEASUREMENT_TYPE_FALLBACK: Record<PnoeMeasurementType, string> = {
  rmr: "RMR",
  amr: "AMR",
};

const PHASE_TYPE_FALLBACK: Record<PnoePhaseType, string> = {
  resting: "Resting",
  warmup: "Warm-up",
  ramp: "Ramp",
  recovery: "Recovery",
};

const MEASUREMENT_STATUS_TONE: Record<PnoeMeasurementStatus, StatusTone> = {
  in_progress: "warning",
  completed: "success",
  invalidated: "danger",
};

const REPORT_STATUS_TONE: Record<PnoeReportStatus, StatusTone> = {
  not_started: "info",
  pending: "warning",
  ready: "success",
  failed: "danger",
};

/** Sentences shown under a status, mirroring the lab-panel status messages. */
const MEASUREMENT_STATUS_MESSAGE: Record<PnoeMeasurementStatus, string> = {
  in_progress:
    "This test is still recording. Complete it to trigger the report calculation, or abort it to discard the results.",
  completed:
    "This test is complete and its results count towards the client's data.",
  invalidated:
    "This test has been invalidated and its report is unpublished. Restore it to bring the results back.",
};

const REPORT_STATUS_MESSAGE: Record<PnoeReportStatus, string> = {
  not_started: "No report has been calculated for this test yet.",
  pending: "The report is being recalculated. Reload to see the result.",
  ready: "The report is ready.",
  failed: "The report calculation failed for this test.",
};

/**
 * Look up a catalog label, falling back to the hardcoded English above and then
 * to the raw value. `catalogs` may be null while the config call is in flight.
 */
const labelFrom = <T extends string>(
  options: { value: T; label: string }[] | undefined,
  value: T | null | undefined,
  fallback: Record<string, string>
): string => {
  if (!value) return "—";
  const fromCatalog = options?.find((option) => option.value === value)?.label;
  return fromCatalog || fallback[value] || value;
};

export const getMeasurementTypeLabel = (
  type: PnoeMeasurementType | null | undefined,
  catalogs?: PnoeMeasurementCatalogs | null
) =>
  labelFrom(catalogs?.measurement_types, type, MEASUREMENT_TYPE_FALLBACK);

export const getPhaseTypeLabel = (
  type: PnoePhaseType | null | undefined,
  catalogs?: PnoeMeasurementCatalogs | null
) => labelFrom(catalogs?.phase_types, type, PHASE_TYPE_FALLBACK);

/**
 * The measurement's own status, as a badge-ready object.
 *
 * `actionRequired` stays false for all three: an in-progress test is a normal
 * state, not something the affiliate must resolve, so it does not earn the
 * colored row accent. A failed *report* does — see `resolveReportStatus`.
 */
export const resolveMeasurementStatus = (
  status: PnoeMeasurementStatus | null | undefined,
  catalogs?: PnoeMeasurementCatalogs | null
): ResolvedStatus | null => {
  if (!status) return null;
  return {
    key: status,
    label: labelFrom(
      catalogs?.measurement_statuses,
      status,
      MEASUREMENT_STATUS_FALLBACK
    ),
    tone: MEASUREMENT_STATUS_TONE[status] ?? "info",
    actionRequired: false,
    message: MEASUREMENT_STATUS_MESSAGE[status] ?? "",
  };
};

/**
 * The report status, as a badge-ready object. `failed` is the one state the
 * affiliate has to act on, so it drives the row accent in the listing.
 */
export const resolveReportStatus = (
  status: PnoeReportStatus | null | undefined,
  catalogs?: PnoeMeasurementCatalogs | null
): ResolvedStatus | null => {
  if (!status) return null;
  return {
    key: status,
    label: labelFrom(
      catalogs?.report_statuses,
      status,
      REPORT_STATUS_FALLBACK
    ),
    tone: REPORT_STATUS_TONE[status] ?? "info",
    actionRequired: status === "failed",
    message: REPORT_STATUS_MESSAGE[status] ?? "",
  };
};

/**
 * Report-status wording for the combined PNOĒ status. The catalog labels
 * ("Not started", "Ready") read wrong once appended to a measurement status, so
 * the design's phrasing lives here — "Completed · Report ready", not
 * "Completed · Ready".
 */
export const PNOE_REPORT_SHORT_LABEL: Record<PnoeReportStatus, string> = {
  not_started: "No report",
  pending: "Report pending",
  ready: "Report ready",
  failed: "Report failed",
};

/**
 * The status a user actually sees: the measurement status, with the report
 * status appended once the test is complete ("Completed · Report ready").
 *
 * Only `completed` carries the report half — an in-progress test has nothing
 * to report on yet, and an invalidated one's report is unpublished, so naming
 * it there would be noise. Those two show the measurement status alone.
 *
 * One resolver for the Assessments row and the detail header, so a test reads
 * the same in the listing and on its own page. The tone comes from the
 * *measurement* (it is the half the affiliate switches); `actionRequired`
 * follows the report only where the report is shown — a failed report is the
 * one state needing attention.
 *
 * `key` is built the same way as `label`, so a status filter keyed on it has
 * exactly one option per distinct label.
 */
export const resolveCompositeMeasurementStatus = (
  status: PnoeMeasurementStatus | null | undefined,
  reportStatus: PnoeReportStatus | null | undefined,
  catalogs?: PnoeMeasurementCatalogs | null
): ResolvedStatus | null => {
  const measurement = resolveMeasurementStatus(status, catalogs);
  if (!measurement) return null;

  const showsReport = status === "completed";
  const report = showsReport ? resolveReportStatus(reportStatus, catalogs) : null;
  const reportSuffix =
    showsReport && reportStatus ? PNOE_REPORT_SHORT_LABEL[reportStatus] : null;

  return {
    ...measurement,
    key: [status, showsReport ? reportStatus : null].filter(Boolean).join(":"),
    label: [measurement.label, reportSuffix].filter(Boolean).join(" · "),
    actionRequired: report?.actionRequired ?? measurement.actionRequired,
  };
};

export type MeasurementActionKind = "complete" | "invalidate";

export interface MeasurementAction {
  kind: MeasurementActionKind;
  /** Button label — the wording changes with the current status. */
  label: string;
}

/**
 * Which transitions are offered, with the doc's wording rules:
 * in_progress → "Complete" / "Abort"; completed → "Invalidate";
 * invalidated → "Restore".
 */
export const getMeasurementActions = (
  status: PnoeMeasurementStatus | null | undefined
): MeasurementAction[] => {
  switch (status) {
    case "in_progress":
      return [
        { kind: "complete", label: "Complete" },
        { kind: "invalidate", label: "Abort" },
      ];
    case "completed":
      return [
        { kind: "invalidate", label: "Invalidate" },
      ];
    case "invalidated":
      return [
        { kind: "complete", label: "Restore" },
      ];
    default:
      return [];
  }
};

export interface MeasurementActionPrompt {
  title: string;
  message: string;
  /** Confirm button label — matches the action's wording for that status. */
  confirmLabel: string;
}

/**
 * The warning shown before a status transition is sent. Every transition has
 * a consequence the user cannot see from the menu, so each gets its own copy:
 *
 * - `in_progress` → anything: the test locks and stops accepting device data,
 *   so the user has to confirm the measurement really is over.
 * - `completed` → `invalidated`: the report is cancelled.
 * - `invalidated` → `completed`: a report is generated.
 *
 * Returns null for a transition the status does not allow, which the menu
 * never offers anyway.
 */
export const getMeasurementActionPrompt = (
  status: PnoeMeasurementStatus | null | undefined,
  action: MeasurementActionKind
): MeasurementActionPrompt | null => {
  const allowed = getMeasurementActions(status).find(
    (candidate) => candidate.kind === action
  );
  if (!allowed) return null;

  switch (status) {
    case "in_progress":
      return {
        title:
          action === "complete" ? "Complete this test?" : "Abort this test?",
        message:
          "Confirm that the measurement has actually finished. Once you change the status, the test is locked and will no longer accept data from the device.",
        confirmLabel: allowed.label,
      };
    case "completed":
      return {
        title: "Invalidate this test?",
        message:
          "The report for this test will be cancelled and will no longer be available.",
        confirmLabel: allowed.label,
      };
    case "invalidated":
      return {
        title: "Restore this test?",
        message: "A new report will be generated for this test.",
        confirmLabel: allowed.label,
      };
    default:
      return null;
  }
};

/**
 * Whether the measurement can be edited at all — it has to be `completed`.
 *
 * Gates the detail page's edit affordances. The restriction is the service's,
 * confirmed against it on both other statuses: a PATCH on an `in_progress`
 * *or* an `invalidated` measurement is refused with "Report inputs can be
 * edited only for a completed measurement". So `completed` is the whole rule —
 * invalidating a test freezes its inputs just as firmly as still recording
 * does.
 */
export const isMeasurementEditable = (
  status: PnoeMeasurementStatus | null | undefined
) => status === "completed";

/**
 * Why this test's report inputs cannot be edited, or null when they can.
 *
 * Names the way out as well as the reason — the two blocked statuses each have
 * a transition that unblocks them, and the status select on the same header is
 * where it happens.
 */
export const getEditLockReason = (
  status: PnoeMeasurementStatus | null | undefined
): string | null => {
  if (isMeasurementEditable(status)) return null;
  return status === "invalidated"
    ? "An invalidated test can't be edited. Set it back to Completed first."
    : "A test that is still recording can't be edited. Complete it first.";
};

/**
 * True while the page should keep polling — only while the test is still
 * recording.
 *
 * A `pending` report deliberately does NOT poll: nothing bounds how long a
 * calculation can sit in that state, so keying on it meant a page left open on a
 * stalled report polled forever. A recording test ends on its own.
 */
export const isMeasurementLive = (
  status: PnoeMeasurementStatus | null | undefined
) => status === "in_progress";

/** `"1234.5"` seconds → `"20:34"` (or `"1:20:34"` past an hour). */
export const formatDurationSeconds = (
  seconds: string | number | null | undefined
): string => {
  const total = toNumber(seconds);
  if (total === null) return "—";
  const whole = Math.max(0, Math.round(total));
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const secs = whole % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(secs)}`
    : `${minutes}:${pad(secs)}`;
};

/** Re-exported so consumers need only this module for a badge. */
export type { StatusTone, ResolvedStatus };
